import { webRoutes } from "@/config/web/routes";
import { Button } from "@/modules/shadcn/ui/button";
import { ModelStatusEnum } from "@repo/schema";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useMatch, useParams } from "react-router";
import { useGetModelsQuery } from "../../services/modelApi";
import { ModelCard } from "../ModelCard";

export interface ModelListProps {
  className?: string;
}

export const ModelList = ({ className }: ModelListProps) => {
  const { t } = useTranslation(["model", "common"]);
  const { projectId } = useParams();
  // Already on the new-version form (possibly mid AI suggestion) — clicking
  // "Create new version" again must not remount it and restart the flow.
  const isCreatingNew = Boolean(useMatch(webRoutes.model.modelNew));
  const { data: models, refetch } = useGetModelsQuery({
    projectId: Number(projectId),
  });

  // Not CONVERTING: nothing writes it any more, and a legacy row stuck on it
  // would poll forever.
  const hasActiveModels =
    models?.some((m) => m.status === ModelStatusEnum.TRAINING) ?? false;

  useEffect(() => {
    if (!hasActiveModels) return;
    const interval = setInterval(() => refetch(), 3000);
    return () => clearInterval(interval);
  }, [hasActiveModels, refetch]);

  return (
    <div
      className={`flex min-h-0 flex-col border-r border-black/10 bg-black/[0.03] py-4 pl-6 pr-2 ${className ?? ""}`}
    >
      <div className="flex flex-shrink-0 flex-col gap-3 pb-3">
        <span className="text-[10px] font-bold uppercase leading-4 tracking-[1px] text-black/90">
          {t("list.versions")}
        </span>
        <Link
          to={`/projects/${projectId}/models/new`}
          aria-disabled={isCreatingNew}
          onClick={(e) => {
            if (isCreatingNew) e.preventDefault();
          }}
        >
          <Button
            variant="outline"
            size="sm"
            disabled={isCreatingNew}
            className="w-full border-emerald-700 bg-transparent text-emerald-700 shadow-none hover:bg-emerald-50 hover:text-emerald-700"
          >
            {t("list.createNewVersion")}
          </Button>
        </Link>
      </div>
      <div className="flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto">
        {models?.toReversed().map((model, i) => (
          <ModelCard key={model.id} model={model} order={models.length - i} />
        ))}
      </div>
    </div>
  );
};
